/**
 * Individual rate API client functions
 *
 * Low-level API functions for individual rate management within schedules.
 */

import { AxiosInstance } from "axios";

import {
  Rate,
  RateCategory,
  RateSchedule,
  RateScheduleInput,
  CreateRateRequest,
  EnrollmentFlagsResponse,
  UpdateRateRequest,
  ReorderRatesRequest,
  ReorderRatesResponse,
} from "../types";

const RATE_SCHEDULES_BASE_URL = "btx/v1/rate-schedules";
const PROPOSAL_RATE_SCHEDULES_BASE_URL = "btx/v1/proposal-rate-schedules";

/**
 * Creates a new rate within a rate schedule
 *
 * POST /api/v1/rate-schedules/:scheduleId/rates
 *
 * @param request - Create rate request
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to created rate
 */
export const createRate = async (
  request: CreateRateRequest,
  axiosInstance: AxiosInstance,
): Promise<Rate> => {
  const { scheduleId, ...body } = request;

  const response = await axiosInstance.post<Rate>(
    `${RATE_SCHEDULES_BASE_URL}/${scheduleId}/rates`,
    body,
  );

  return response.data;
};

/**
 * Updates an individual rate's mutable fields
 *
 * PATCH /api/v1/rate-schedules/:scheduleId/rates/:rateId
 *
 * @param request - Update rate request
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to updated rate
 */
export const updateRate = async (
  request: UpdateRateRequest,
  axiosInstance: AxiosInstance,
): Promise<Rate> => {
  const { scheduleId, rateId, rate } = request;

  const response = await axiosInstance.patch<Rate>(
    `${RATE_SCHEDULES_BASE_URL}/${scheduleId}/rates/${rateId}`,
    rate,
  );

  return response.data;
};

export const replaceRateSchedules = async (
  benefitPlanId: string,
  rateSchedules: RateScheduleInput[],
  axiosInstance: AxiosInstance,
  category?: RateCategory,
): Promise<RateSchedule[]> => {
  const query = category
    ? `?benefitPlanId=${benefitPlanId}&category=${category}`
    : `?benefitPlanId=${benefitPlanId}`;

  const response = await axiosInstance.put<{ rateSchedules: RateSchedule[] }>(
    `${RATE_SCHEDULES_BASE_URL}${query}`,
    { rateSchedules },
  );

  return response.data.rateSchedules;
};

export const replaceProposalRateSchedules = async (
  proposalPlanId: string,
  rateSchedules: RateScheduleInput[],
  axiosInstance: AxiosInstance,
): Promise<RateSchedule[]> => {
  const response = await axiosInstance.put<{ rateSchedules: RateSchedule[] }>(
    `${PROPOSAL_RATE_SCHEDULES_BASE_URL}?proposalPlanId=${proposalPlanId}`,
    { rateSchedules },
  );

  return response.data.rateSchedules;
};

/** A saved proposal rate, with the plan group's flags where the server could report them. */
export interface SavedProposalRate extends Rate {
  enrollmentFlags?: EnrollmentFlagsResponse;
}

export const updateProposalRate = async (
  request: UpdateRateRequest,
  axiosInstance: AxiosInstance,
): Promise<SavedProposalRate> => {
  const { scheduleId, rateId, rate } = request;

  const response = await axiosInstance.patch<SavedProposalRate>(
    `${PROPOSAL_RATE_SCHEDULES_BASE_URL}/${scheduleId}/rates/${rateId}`,
    rate,
  );

  return response.data;
};

/**
 * Deletes an individual rate
 *
 * DELETE /api/v1/rate-schedules/:scheduleId/rates/:rateId
 *
 * @param scheduleId - Rate schedule ID
 * @param rateId - Rate ID to delete
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving when deletion is complete
 */
export const deleteRate = async (
  scheduleId: string,
  rateId: string,
  axiosInstance: AxiosInstance,
): Promise<void> => {
  await axiosInstance.delete(
    `${RATE_SCHEDULES_BASE_URL}/${scheduleId}/rates/${rateId}`,
  );
};

/**
 * Reorders rates within a rate schedule
 *
 * PATCH /api/v1/rate-schedules/:scheduleId/rates/reorder
 *
 * @param request - Reorder request with schedule ID and ordered rate IDs
 * @param axiosInstance - Axios instance for API calls
 * @returns Promise resolving to reordered rates
 */
export const reorderRates = async (
  request: ReorderRatesRequest,
  axiosInstance: AxiosInstance,
): Promise<ReorderRatesResponse> => {
  const { scheduleId, rates } = request;

  const response = await axiosInstance.patch<ReorderRatesResponse>(
    `${RATE_SCHEDULES_BASE_URL}/${scheduleId}/rates/reorder`,
    { rates },
  );

  return response.data;
};
